import React from 'react';
import PropTypes from "prop-types";
import Helmet from "react-helmet";
import styled from "@emotion/styled";
import colors from "styles/colors";
import { Link, graphql } from 'gatsby';
import { RichText } from "prismic-reactjs";

import Button from "components/_ui/Button";
import Layout from "components/Layout";

const ProjectHeroContainer = styled("div")`
    background: ${colors.white};
    display: flex;
    justify-content: center;
    align-items: flex-end;
    overflow: hidden;
    position: relative;
    padding-top: 2.25em;
    margin-bottom: 3.5em;

    img {
        max-width: 768px;
    }
`

const ProjectTitle = styled("div") `
    max-width: 640px;
    margin: 0 auto;
    text-align: center;
`

const ProjectCopy = styled("div")`
    max-width: 800px;
    margin: 0 auto;
    padding: 0 2.5%;

    p {
      line-height: 1.9;
    }

    h2, h3 {
        text-align: center;
        margin: 0 auto;
    }

    h3 {
        margin-top: .5em;
        font-weight: 500;
        color: ${colors.grey600};
    }

    img {
        width: 100%;
        margin: 2em auto;
    }

    div.challenge {
        h2 {
            color: ${colors.blue500};
        }
    }

    div.process {
        h2 {
            color: ${colors.green500};
        }
    }

    div.solution {
        h2 {
            color: ${colors.purple700};
        }
    }

    div.results {
        h2 {
            color: ${colors.orange500};
        }
    }

`

const WorkLink = styled(Link)`
    margin-top: 3em;
    display: block;
    text-align: center;
`


const Project = ({ project, meta }) => {
    return (
        <>
            <Helmet
                title={`${project.title[0].text} | `}
                titleTemplate={`%s | ${meta.title}`}
                meta={[
                    {
                        name: `description`,
                        content: meta.description,
                    },
                    {
                        property: `og:title`,
                        content: `${project.title[0].text} | `,
                    },
                    {
                        property: `og:description`,
                        content: meta.description,
                    },
                    {
                        property: `og:type`,
                        content: `website`,
                    },
                    {
                        name: `twitter:card`,
                        content: `summary`,
                    },
                    {
                        name: `twitter:creator`,
                        content: meta.author,
                    },
                    {
                        name: `twitter:title`,
                        content: meta.title,
                    },
                    {
                        name: `twitter:description`,
                        content: meta.description,
                    },
                ].concat(meta)}
            />
            <Layout>
                <ProjectTitle>
                    {RichText.render(project.title)}
                </ProjectTitle>
                {project.hero && (
                    <ProjectHeroContainer>
                        <img src={project.hero.url} alt={project.hero.alt} />
                    </ProjectHeroContainer>
                )}
                <ProjectCopy>
                    <div className="challenge">
                        <h2>The Challenge</h2>
                        <h3>{RichText.asText(project.challenge_subtitle)}</h3>
                        {RichText.render(project.challenge_copy)}
                        <img src={project.challenge_image.url} alt={project.challenge_image.alt} />
                    </div>

                    <div className="process">
                        <h2>The Process</h2>
                        <h3>{RichText.asText(project.process_subtitle)}</h3>
                        {RichText.render(project.process_copy)}
                        <img src={project.process_image.url} alt={project.process_image.alt} />
                    </div>

                    <div className="solution">
                        <h2>The Solution</h2>
                        <h3>{RichText.asText(project.solution_subtitle)}</h3>
                        {RichText.render(project.solution_copy)}
                        <img src={project.solution_image.url} alt={project.solution_image.alt} />
                    </div>
                    <div className="results">
                        <h2>The Results</h2>
                        <h3>{RichText.asText(project.results_subtitle)}</h3>
                        {RichText.render(project.results_copy)}
                        <img src={project.results_image.url} alt={project.results_image.alt} />
                    </div>
                </ProjectCopy>
                <WorkLink to={"/work"}>
                    <Button>
                        See our other work
                    </Button>
                </WorkLink>
            </Layout>
        </>
    )
}

export default ({ data }) => {
    const projectContent = data.prismic.allProjects.edges[0].node;
    const meta = data.site.siteMetadata;
    return (
        <Project project={projectContent} meta={meta}/>
    )
}

Project.propTypes = {
    project: PropTypes.object.isRequired,
};

export const query = graphql`
    query ProjectQuery($uid: String) {
        prismic {
            allProjects(uid: $uid) {
                edges {
                    node {
                        title
                        subtitle
                        preview_description
                        thumbnail
                        hero
                        challenge_subtitle
                        challenge_copy
                        challenge_image
                        process_subtitle
                        process_copy
                        process_image
                        solution_subtitle
                        solution_copy
                        solution_image
                        results_subtitle
                        results_copy
                        results_image
                        meta_title
                        meta_description
                        og_title
                        og_description
                        og_image
                        _meta {
                            uid
                        }
                    }
                }
            }
        }
        site {
            siteMetadata {
                title
                description
                author
            }
        }
    }
`